import React, { useEffect, useState } from "react";
import { Clock, MapPin, ChevronRight, Gavel, HeartHandshake, CalendarDays } from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtTime } from "@/components/PageHeader";

const PILL = {
  conference: ["Conference", "bg-blue-100 text-blue-700"],
  volunteer: ["Volunteer", "bg-indigo-100 text-indigo-700"],
  event: ["Event", "bg-emerald-100 text-emerald-700"],
};

function PreviewRow({ it, actions, tint }) {
  const [label, cls] = it.source === "external" ? ["Imported", "bg-amber-100 text-amber-700"] : PILL[it.type] || PILL.event;
  const Icon = it.type === "conference" ? Gavel : it.type === "volunteer" ? HeartHandshake : CalendarDays;
  return (
    <div className={`flex items-start gap-3 rounded-xl border border-slate-100 p-3 ${tint || "bg-white"}`} data-testid={`day-preview-item-${it.id}`} data-tinted={tint ? "true" : undefined}>
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-slate-800">{it.title}</p>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>{label}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
          {it.all_day ? <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> All day</span>
            : it.start_time && <span className="flex items-center gap-1 tabular-nums"><Clock className="h-3 w-3" /> {fmtTime(it.start_time)}{it.end_time ? ` – ${fmtTime(it.end_time)}` : ""}</span>}
          {it.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {it.location}</span>}
        </div>
        {actions && <div className="mt-2 flex flex-wrap items-center gap-1.5 empty:hidden" data-testid={`day-preview-actions-${it.id}`}>{actions}</div>}
      </div>
    </div>
  );
}

const DESKTOP = "(min-width: 768px)";
function useIsDesktop() {
  const [m, setM] = useState(() => window.matchMedia(DESKTOP).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP);
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
}

// Day preview: bottom sheet on phones, centered popup on desktop; "View day details" jumps to the monthly list.
export default function DayPreviewSheet({ open, title, items, onClose, onViewDetails, renderActions, tintFor }) {
  const desktop = useIsDesktop();
  const count = `${items.length} item${items.length === 1 ? "" : "s"} scheduled`;
  const list = (
    <div className={`space-y-2 overflow-y-auto ${desktop ? "max-h-[60vh] pr-1" : "px-4"}`} data-testid="day-preview-list">
      {items.map((it) => <PreviewRow key={it.id} it={it} actions={renderActions?.(it)} tint={tintFor?.(it)} />)}
    </div>
  );
  const details = (
    <Button className="h-11 w-full rounded-full" onClick={onViewDetails} data-testid="day-preview-details-btn">
      View day details <ChevronRight className="ml-1 h-4 w-4" />
    </Button>
  );
  if (desktop) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-lg" data-testid="day-preview-sheet">
          <DialogHeader>
            <DialogTitle className="font-heading" data-testid="day-preview-title">{title}</DialogTitle>
            <DialogDescription>{count}</DialogDescription>
          </DialogHeader>
          {list}
          <DialogFooter>{details}</DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Drawer open={open} onOpenChange={(v) => !v && onClose()}>
      <DrawerContent className="max-h-[80vh]" data-testid="day-preview-sheet">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-heading" data-testid="day-preview-title">{title}</DrawerTitle>
          <DrawerDescription>{count}</DrawerDescription>
        </DrawerHeader>
        {list}
        <DrawerFooter>{details}</DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
